import { Banner, type BannerProps } from '@imprivata-cloud/components';
import { Fragment, type PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { errorConfiguration } from '../ErrorConfig';
import { SESSION_ERROR_CODES } from '../constants';
import { useErrorContext } from './ErrorContext';

export type NotificationProps = { translationRoot?: string; mountNode?: string };

export const Notifications = ({
  translationRoot,
  mountNode,
  children,
}: PropsWithChildren<NotificationProps>) => {
  const { t } = useTranslation();
  const { error, clearErrorNotifications, successMessage, clearSuccessNotification } =
    useErrorContext();

  const errorBanner = useMemo(() => {
    if (!error) {
      return undefined;
    }
    const errorCode = error.code || error.details?.code;
    if (errorCode && SESSION_ERROR_CODES.includes(errorCode)) {
      return undefined;
    }
    const errorConfig =
      errorConfiguration && errorCode in errorConfiguration
        ? errorConfiguration[errorCode]
        : undefined;
    let bannerMsg: BannerProps;
    if (errorConfig) {
      // Create i18n key for error message lookup
      const errorKey = translationRoot
        ? `${translationRoot}.errors.${errorConfig.errorCode}`
        : `errors.${errorConfig.errorCode}`;
      bannerMsg = {
        key: errorKey,
        type: errorConfig.type,
        message: t(errorKey as never, { defaultValue: t('errors.something-unexpected') }),
      };
    } else {
      bannerMsg = {
        key: 'unknown-error',
        type: 'error',
        message: t('errors.something-unexpected'),
      };
    }
    return bannerMsg;
  }, [t, translationRoot, error]);

  const successBanner: BannerProps | undefined = successMessage
    ? { key: 'success-notification', type: 'success', message: successMessage }
    : undefined;

  const banner = errorBanner ?? successBanner;
  const onClose = errorBanner ? clearErrorNotifications : clearSuccessNotification;

  return (
    <>
      {banner && (
        <Banner
          key={banner.key}
          datatestid="notification-banner"
          onClose={onClose}
          message={banner.message}
          type={banner.type}
          duration={10}
          mountNode={mountNode}
        />
      )}
      <Fragment key={'initial-notif-render'}>{children}</Fragment>
    </>
  );
};
